import { Droplet, CupSoda, Snowflake, Stethoscope, HardHat } from 'lucide-react';

export const industries = [
  {
    id: 'ro-servicing',
    icon: Droplet,
    name: 'RO & Water Purifier Servicing',
    tagline: 'Never miss a filter change again.',
    painPoints: [
      'Filter and membrane change dates live in a technician’s notebook or nowhere at all, so AMC visits get missed until the customer complains.',
      'Renewals are chased manually, one phone call at a time, so a chunk of AMC revenue quietly lapses every year.',
      'A customer calls about a leaking unit and the office has no record of which technician last serviced it or what parts are in their machine.',
      'Technicians run out of filters or cartridges mid-route because nobody tracks van/warehouse stock in real time.',
    ],
    howWeHelp: [
      {
        module: 'Subscription Orders',
        detail: 'Every AMC contract becomes a recurring subscription that auto-generates the next service visit on schedule — quarterly filter changes, annual membrane replacements — with automatic billing rolled up per customer. No visit is ever missed because it wasn’t written down.',
      },
      {
        module: 'Appointments',
        detail: 'Customers book a repair or installation slot over a call or WhatsApp, and the AI voice bot can handle the booking without tying up your office phone.',
      },
      {
        module: 'Workforce Management (Tecnvi Ongo)',
        detail: 'Technicians see their day’s route on their phone, update job status live, and attach photos of the completed job — so the office always knows what happened without a callback.',
      },
      {
        module: 'Products & Inventory',
        detail: 'Filters, membranes, and spare parts are tracked with reorder alerts, so a technician’s van is stocked before it becomes a problem on-site.',
      },
    ],
  },
  {
    id: 'vending-machines',
    icon: CupSoda,
    name: 'Vending Machine Operators',
    tagline: 'Every machine, restocked on schedule, every time.',
    painPoints: [
      'Machines run empty between refill rounds because restocking runs on guesswork instead of real consumption data.',
      'A fleet of machines across a city means route planning by memory — and technicians backtracking across town.',
      'Cash and card collection reconciliation across dozens of machines becomes a monthly headache.',
      'No visibility into which machine is jammed, empty, or out of service until a customer complains on-site.',
    ],
    howWeHelp: [
      {
        module: 'Subscription Orders',
        detail: 'Set a refill cadence per machine or location and the system generates the next restock job automatically — the same recurring-delivery engine that powers daily subscription businesses, applied to your machine fleet.',
      },
      {
        module: 'Workforce Management (Tecnvi Ongo)',
        detail: 'Refill routes are assigned to specific staff with each stop’s machine, location, and expected stock — completed with a status update and photo the moment the round is done.',
      },
      {
        module: 'Products & Inventory',
        detail: 'Track per-SKU stock, low-stock thresholds, and warehouse-to-route stock movement, so a route is never dispatched half-stocked.',
      },
      {
        module: 'Orders',
        detail: 'Every refill and repair job is logged as an order with a full history — useful when a machine needs an audit trail for maintenance or billing disputes.',
      },
    ],
  },
  {
    id: 'ac-fridge-repair',
    icon: Snowflake,
    name: 'AC & Refrigerator Repair',
    tagline: 'From the first call to the closed job, in one system.',
    painPoints: [
      'Peak summer call volume overwhelms a small office team trying to book, confirm, and reshuffle technician visits by phone.',
      'Repeat customers call back with a similar issue and the technician has no history of previous repairs or parts used.',
      'Technicians are dispatched without knowing the fault reported, wasting a visit if the wrong part is carried.',
      'Spare part costs and labor charges are tallied by hand after the job, delaying invoicing and creating billing errors.',
    ],
    howWeHelp: [
      {
        module: 'Appointments',
        detail: 'The AI voice bot answers repair calls, books a slot into the right technician’s day, and sends automatic confirmation and reminder messages — even during a summer call surge.',
      },
      {
        module: 'Workforce Management (Tecnvi Ongo)',
        detail: 'The assigned technician gets the job with customer, address, and reported fault on their phone, updates status from accepted to in-progress to completed, and logs notes and photos as proof of work.',
      },
      {
        module: 'Customers',
        detail: 'Every unit’s repair history is tied to the customer record, so a technician walks in already knowing what was serviced last time.',
      },
      {
        module: 'Products & Services',
        detail: 'Spare parts and service charges are cataloged with pricing, so the job converts straight into an accurate invoice with no manual tallying.',
      },
    ],
  },
  {
    id: 'medical-equipment',
    icon: Stethoscope,
    name: 'Medical Equipment & Tools Repair',
    tagline: 'Compliance-ready service records for critical equipment.',
    painPoints: [
      'Hospitals and clinics expect documented, auditable service history for every device — lost paperwork is a compliance risk, not just an inconvenience.',
      'AMC contracts on ventilators, monitors, and diagnostic tools need strict, provable maintenance schedules.',
      'Certified technicians must be matched to the right equipment type, which is hard to track manually across a large client base.',
      'B2B clients (hospitals, labs, clinics) need proper documentation, tax paperwork, and multi-site/multi-contact account handling — not simple one-off consumer billing.',
    ],
    howWeHelp: [
      {
        module: 'Customers (B2B)',
        detail: 'Hospital and clinic accounts are managed as B2B customers with document storage for compliance paperwork, multiple linked contacts per site, and full verification — built for institutional clients, not just individual consumers.',
      },
      {
        module: 'Subscription Orders',
        detail: 'AMC/maintenance contracts on critical equipment become recurring service schedules with automatic visit generation and consolidated monthly billing — a defensible, timestamped maintenance trail for every device.',
      },
      {
        module: 'Appointments',
        detail: 'Service and inspection visits are booked and confirmed against technician availability, with reminders so scheduled maintenance windows are never missed.',
      },
      {
        module: 'Workforce Management (Tecnvi Ongo)',
        detail: 'Technicians close out each visit with notes and photo evidence attached to the job record — the audit trail compliance teams ask for, generated automatically as a byproduct of doing the work.',
      },
    ],
  },
  {
    id: 'civil-construction',
    icon: HardHat,
    name: 'Civil Construction',
    tagline: 'From site visit to sign-off, without the paper trail getting lost.',
    painPoints: [
      'Site visits, inspections, and progress checks are tracked on paper or scattered chat messages, making it hard to prove what was done and when.',
      'Coordinating multiple crews and contractors across active sites is a scheduling and accountability challenge.',
      'Material orders and vendor deliveries to site are tracked separately from the work itself, causing delays and mismatched stock.',
      'Clients want visibility into project progress and sign-off documentation without having to call the site office every time.',
    ],
    howWeHelp: [
      {
        module: 'Workforce Management (Tecnvi Ongo)',
        detail: 'Site engineers and crews receive assigned site visits and tasks on their phone, update progress status in real time, and attach photos as an on-record inspection or completion report — exactly the site-inspection and reporting workflow construction teams need.',
      },
      {
        module: 'Appointments',
        detail: 'Site visits, inspections, and client walkthroughs are scheduled and confirmed like any other appointment, with automatic reminders to crews and clients.',
      },
      {
        module: 'Orders',
        detail: 'Material and vendor orders tied to a site or project are tracked from request to delivery, so procurement and on-site work stay in sync.',
      },
      {
        module: 'Customers (B2B)',
        detail: 'Manage project owners, contractors, and vendors as distinct account types with documents, contracts, and multi-contact support for larger commercial projects.',
      },
    ],
  },
];
